import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { driversAPI, consentAPI, clientsAPI } from '../services/api';
import NamedDriverPolicyBanner from '../components/NamedDriverPolicyBanner';
import { useAuth } from '../contexts/AuthContext';
import { formatDateBritish } from '../utils/dateUtils';
import { DVLA_PENALTY_CODES, DVLA_CATEGORIES, getPenaltyCodeDetails } from '../data/dvlaPenaltyCodes';
import './DriverList.css';
import additionIcon from '../assets/icons/addition.png';

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface AccidentClaim {
  date: string;
  circumstance: string;
  claimType: string;
  cost: string;
}

interface Driver {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  licenseNumber?: string;
  licenseExpiry?: string;
  licenceConsentGiven: boolean;
  licenceConsentDate?: string;
  address?: string;
  restrictions?: string;
  outstandingPoints?: number;
  penaltyCodes?: string;
  clientId: number;
  client?: { id: number; companyName: string };
  isActive: boolean;
  referralStatus?: string;
  accidentHistory?: string;
  createdAt: string;
  updatedAt: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  licenseNumber: string;
  licenseExpiry: string;
  licenceConsentGiven: boolean;
  licenceConsentDate: string;
  address: string;
  restrictions: string;
  outstandingPoints: string;
  penaltyCodes: string[];
  clientId: number;
  hasAccidentHistory: '' | 'yes' | 'no';
  accidentClaims: AccidentClaim[];
  hasNotifiableConditions: '' | 'yes' | 'no';
  hasUnspentConvictions: '' | 'yes' | 'no';
  dvlaVerificationType: '' | 'document' | 'code';
  dvlaCheckCode: string;
}

const EMPTY_FORM: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  licenseNumber: '',
  licenseExpiry: '',
  licenceConsentGiven: false,
  licenceConsentDate: '',
  address: '',
  restrictions: '',
  outstandingPoints: '',
  penaltyCodes: [],
  clientId: 0,
  hasAccidentHistory: '',
  accidentClaims: [],
  hasNotifiableConditions: '',
  hasUnspentConvictions: '',
  dvlaVerificationType: '',
  dvlaCheckCode: '',
};

// ─── Component ────────────────────────────────────────────────────────────────

const DriverList: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const clientIdFilter = searchParams.get('clientId');

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);

  const [showDriverPolicyBanner, setShowDriverPolicyBanner] = useState(true);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [pendingReferrals, setPendingReferrals] = useState<Driver[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const [showLicenceConsent, setShowLicenceConsent] = useState(false);
  const [licenceConsentAccepted, setLicenceConsentAccepted] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCode, setSelectedCode] = useState('');

  // Licence image state (edit mode only)
  const [licenceFront, setLicenceFront] = useState<string | null>(null);
  const [licenceBack, setLicenceBack] = useState<string | null>(null);
  const [licenceImgUploading, setLicenceImgUploading] = useState<'front' | 'back' | null>(null);
  const [licenceImgError, setLicenceImgError] = useState<string | null>(null);
  const licenceFrontRef = useRef<HTMLInputElement>(null);
  const licenceBackRef = useRef<HTMLInputElement>(null);
  const [currentClaim, setCurrentClaim] = useState<AccidentClaim>({ date: '', circumstance: '', claimType: '', cost: '' });

  // New driver (add mode) licence image files — uploaded immediately after creation
  const [newFrontFile, setNewFrontFile] = useState<File | null>(null);
  const [newBackFile, setNewBackFile]   = useState<File | null>(null);
  const [newFrontPreview, setNewFrontPreview] = useState<string | null>(null);
  const [newBackPreview, setNewBackPreview]   = useState<string | null>(null);
  const newFrontRef = useRef<HTMLInputElement>(null);
  const newBackRef  = useRef<HTMLInputElement>(null);

  // DVLA Summary Document upload
  const [dvlaDocFile, setDvlaDocFile]       = useState<File | null>(null);
  const [dvlaDocFileName, setDvlaDocFileName] = useState<string>('');
  const dvlaDocRef = useRef<HTMLInputElement>(null);

  // Unspent convictions info popup
  const [showConvictionsPopup, setShowConvictionsPopup] = useState(false);

  // Licence image viewer (driver table)
  const [licenceView, setLicenceView] = useState<{
    driverId: number;
    driverName: string;
    front: string | null;
    back: string | null;
    loading: boolean;
  } | null>(null);

  // ─── Data loading

  const loadDrivers = useCallback(async () => {
    try {
      const response = await driversAPI.getAll();
      let active = response.data.filter((d: Driver) => d.isActive !== false && d.referralStatus !== 'Pending');
      if (clientIdFilter) {
        active = active.filter((d: Driver) => d.clientId === parseInt(clientIdFilter));
      }
      setDrivers(active);
    } catch (error) {
      console.error('Error loading drivers:', error);
    } finally {
      setLoading(false);
    }
  }, [clientIdFilter]);

  useEffect(() => {
    loadDrivers();
  }, [loadDrivers]);

  // Load the client's driver policy banner setting
  useEffect(() => {
    const cid = clientIdFilter ? parseInt(clientIdFilter) : user?.clientId;
    if (!cid) return;
    clientsAPI.getById(cid)
      .then(r => setShowDriverPolicyBanner(r.data.showDriverPolicyBanner !== false))
      .catch(() => {}); // fail silently — default true
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIdFilter, user?.clientId]);

  // Show welcome popup on every page load
  useEffect(() => {
    setShowWelcomePopup(true);
  }, []);

  // Load pending referrals — filtered to the current client when a clientId filter is active
  const loadPendingReferrals = useCallback(async () => {
    try {
      const res = await driversAPI.getPendingReferrals();
      let pending: Driver[] = res.data;
      if (clientIdFilter) {
        pending = pending.filter((d: Driver) => d.clientId === parseInt(clientIdFilter));
      }
      setPendingReferrals(pending);
    } catch { /* non-critical */ }
  }, [clientIdFilter]);

  useEffect(() => {
    loadPendingReferrals();
  }, [loadPendingReferrals]);

  // ─── Form helpers ──────────────────────────────────────────────────────────

  const loadLicenceImages = async (driverId: number) => {
    try {
      const res = await driversAPI.getLicenceImages(driverId);
      setLicenceFront(res.data.frontBase64 || null);
      setLicenceBack(res.data.backBase64 || null);
    } catch { /* non-critical */ }
  };

  const openAdd = () => {
    setEditingDriver(null);
    setFormData({ ...EMPTY_FORM, clientId: (clientIdFilter ? parseInt(clientIdFilter) : user?.clientId) || 0 });
    setLicenceConsentAccepted(false);
    setLicenceFront(null);
    setLicenceBack(null);
    setLicenceImgError(null);
    setNewFrontFile(null); setNewBackFile(null);
    setNewFrontPreview(null); setNewBackPreview(null);
    setDvlaDocFile(null); setDvlaDocFileName('');
    setFormError(null);
    setCurrentClaim({ date: '', circumstance: '', claimType: '', cost: '' });
    setShowForm(true);
  };

  const handleNewFileSelect = (e: React.ChangeEvent<HTMLInputElement>, side: 'front' | 'back') => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) { setLicenceImgError('Only PNG, JPEG, WebP or GIF supported.'); return; }
    if (file.size > 5 * 1024 * 1024) { setLicenceImgError('File must be under 5 MB.'); return; }
    setLicenceImgError(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (side === 'front') { setNewFrontFile(file); setNewFrontPreview(ev.target?.result as string); }
      else                  { setNewBackFile(file);  setNewBackPreview(ev.target?.result as string); }
    };
    reader.readAsDataURL(file);
  };

  const handleEdit = (driver: Driver) => {
    setEditingDriver(driver);
    setLicenceConsentAccepted(driver.licenceConsentGiven);
    setLicenceFront(null);
    setLicenceBack(null);
    setLicenceImgError(null);
    loadLicenceImages(driver.id);
    setFormData({
      hasNotifiableConditions: (driver as any).hasNotifiableConditions === true ? 'yes' : (driver as any).hasNotifiableConditions === false ? 'no' : '',
      hasUnspentConvictions: (driver as any).hasUnspentConvictions === true ? 'yes' : (driver as any).hasUnspentConvictions === false ? 'no' : '',
      firstName: driver.firstName,
      lastName: driver.lastName,
      email: driver.email,
      phone: driver.phone,
      licenseNumber: driver.licenseNumber || '',
      licenseExpiry: driver.licenseExpiry ? driver.licenseExpiry.split('T')[0] : '',
      licenceConsentGiven: driver.licenceConsentGiven,
      licenceConsentDate: driver.licenceConsentDate || '',
      address: driver.address || '',
      restrictions: driver.restrictions || '',
      outstandingPoints: driver.outstandingPoints != null ? String(driver.outstandingPoints) : '',
      penaltyCodes: driver.penaltyCodes
        ? driver.penaltyCodes.split(',').map(c => c.trim()).filter(Boolean)
        : [],
      clientId: driver.clientId,
      hasAccidentHistory: driver.accidentHistory ? 'yes' : '',
      accidentClaims: (() => { try { return driver.accidentHistory ? JSON.parse(driver.accidentHistory) : []; } catch { return []; } })(),
      dvlaVerificationType: '',
      dvlaCheckCode: '',
    });
    setCurrentClaim({ date: '', circumstance: '', claimType: '', cost: '' });
    setFormError(null);
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingDriver(null);
    setFormData(EMPTY_FORM);
    setFormError(null);
    setLicenceConsentAccepted(false);
    setLicenceFront(null);
    setLicenceBack(null);
    setLicenceImgError(null);
    setNewFrontFile(null); setNewBackFile(null);
    setNewFrontPreview(null); setNewBackPreview(null);
    setDvlaDocFile(null); setDvlaDocFileName('');
    setSelectedCategory('');
    setSelectedCode('');
    setShowConvictionsPopup(false);
  };

  const handleLicenceImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, side: 'front' | 'back') => {
    const file = e.target.files?.[0];
    if (!file || !editingDriver) return;
    setLicenceImgError(null);
    const allowed = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) { setLicenceImgError('Only PNG, JPEG, WebP or GIF supported.'); return; }
    if (file.size > 5 * 1024 * 1024) { setLicenceImgError('File must be under 5 MB.'); return; }
    setLicenceImgUploading(side);
    try {
      if (side === 'front') {
        const res = await driversAPI.uploadLicenceFront(editingDriver.id, file);
        setLicenceFront(res.data.imageBase64);
      } else {
        const res = await driversAPI.uploadLicenceBack(editingDriver.id, file);
        setLicenceBack(res.data.imageBase64);
      }
    } catch { setLicenceImgError('Upload failed. Please try again.'); }
    finally {
      setLicenceImgUploading(null);
      if (side === 'front' && licenceFrontRef.current) licenceFrontRef.current.value = '';
      if (side === 'back' && licenceBackRef.current) licenceBackRef.current.value = '';
    }
  };

  const handleLicenceImageDelete = async (side: 'front' | 'back') => {
    if (!editingDriver) return;
    try {
      if (side === 'front') { await driversAPI.deleteLicenceFront(editingDriver.id); setLicenceFront(null); }
      else { await driversAPI.deleteLicenceBack(editingDriver.id); setLicenceBack(null); }
    } catch { setLicenceImgError('Failed to remove image.'); }
  };

  const handleViewLicenceImages = async (driver: Driver) => {
    setLicenceView({ driverId: driver.id, driverName: `${driver.firstName} ${driver.lastName}`, front: null, back: null, loading: true });
    try {
      const res = await driversAPI.getLicenceImages(driver.id);
      setLicenceView(prev =>
        prev?.driverId === driver.id
          ? { ...prev, front: res.data.frontBase64 || null, back: res.data.backBase64 || null, loading: false }
          : prev
      );
    } catch {
      setLicenceView(prev => prev?.driverId === driver.id ? { ...prev, loading: false } : prev);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (formData.licenseNumber && !licenceConsentAccepted) {
      setFormError('You must accept the driving licence data policy before adding a licence number.');
      return;
    }

    const payload = {
      ...formData,
      licenseNumber: formData.licenseNumber || null,
      licenseExpiry: formData.licenseExpiry || null,
      licenceConsentGiven: licenceConsentAccepted,
      licenceConsentDate: licenceConsentAccepted ? (formData.licenceConsentDate || new Date().toISOString()) : null,
      outstandingPoints: formData.outstandingPoints !== '' ? parseInt(formData.outstandingPoints) : null,
      penaltyCodes: formData.penaltyCodes.join(',') || null,
      accidentHistory: formData.hasAccidentHistory === 'yes' && formData.accidentClaims.length > 0
        ? JSON.stringify(formData.accidentClaims)
        : formData.hasAccidentHistory === 'yes' ? '[]' : null,
      hasNotifiableConditions: formData.hasNotifiableConditions === 'yes' ? true : formData.hasNotifiableConditions === 'no' ? false : null,
      hasUnspentConvictions: formData.hasUnspentConvictions === 'yes' ? true : formData.hasUnspentConvictions === 'no' ? false : null,
    };

    try {
      if (editingDriver) {
        await driversAPI.update(editingDriver.id, { ...payload, id: editingDriver.id });
      } else {
        const res = await driversAPI.create(payload);
        const created = res.data;
        // Upload staged licence images immediately after creation (fire-and-forget)
        if (newFrontFile) { driversAPI.uploadLicenceFront(created.id, newFrontFile).catch(() => {}); }
        if (newBackFile)  { driversAPI.uploadLicenceBack(created.id, newBackFile).catch(() => {}); }
        loadDrivers();
        resetForm();
      }
      loadDrivers();
      resetForm();
    } catch (error: any) {
      setFormError(error.response?.data?.message || 'Failed to save driver. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to remove this named driver?')) return;
    try {
      await driversAPI.delete(id);
      setDrivers(drivers.filter(d => d.id !== id));
    } catch {
      alert('Failed to delete driver.');
      loadDrivers();
    }
  };

  // ─── Licence consent ───────────────────────────────────────────────────────

  const handleLicenceFieldFocus = () => {
    if (!licenceConsentAccepted) setShowLicenceConsent(true);
  };

  const handleConsentAccept = () => {
    setLicenceConsentAccepted(true);
    setFormData(prev => ({ ...prev, licenceConsentGiven: true, licenceConsentDate: new Date().toISOString() }));
    setShowLicenceConsent(false);
    // Record driver licence consent for audit trail (fire-and-forget)
    const driverName = `${formData.firstName} ${formData.lastName}`.trim();
    consentAPI.log({
      consentType: 'DRIVER_LICENCE',
      clientId: formData.clientId || user?.clientId || null,
      driverName: driverName || undefined,
      details: `Driving licence data policy accepted${driverName ? ` for driver: ${driverName}` : ''}`,
    }).catch(() => {});
  };

  const handleConsentDecline = () => {
    setShowLicenceConsent(false);
    setFormData(prev => ({ ...prev, licenseNumber: '', licenseExpiry: '' }));
  };

  // ─── Penalty code picker ───────────────────────────────────────────────────

  const codesForCategory = selectedCategory
    ? DVLA_PENALTY_CODES.filter(c => c.category === selectedCategory)
    : [];

  const addPenaltyCode = () => {
    if (!selectedCode || formData.penaltyCodes.includes(selectedCode)) return;
    setFormData(prev => ({ ...prev, penaltyCodes: [...prev.penaltyCodes, selectedCode] }));
    setSelectedCode('');
  };

  const removePenaltyCode = (code: string) => {
    setFormData(prev => ({ ...prev, penaltyCodes: prev.penaltyCodes.filter(c => c !== code) }));
  };

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const isLicenseExpiring = (expiryDate?: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const soon = new Date();
    soon.setDate(soon.getDate() + 30);
    return expiry <= soon;
  };

  const parseCodes = (csv?: string) =>
    csv ? csv.split(',').map(c => c.trim()).filter(Boolean) : [];

  if (loading) return <div>Loading named drivers...</div>;

  return (
    <div className="driver-list">
      {/* Policy banner — only shown when enabled for this client */}
      {showDriverPolicyBanner && <NamedDriverPolicyBanner isAdmin={isAdmin} />}

      {/* Page header */}
      <div className="page-header">
        <h1>Referred Named Drivers</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <img src={additionIcon} alt="" style={{ width: '16px', height: '16px', marginRight: '8px' }} />
          Add Referred Named Driver
        </button>
      </div>

      {/* Welcome popup — shown on every page load */}
      {showWelcomePopup && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Referred Named Drivers</h2>
              <button className="close-btn" onClick={() => setShowWelcomePopup(false)}>✕</button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ backgroundColor: '#fef3c7', border: '2px solid #f59e0b', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#92400e', lineHeight: 1.5 }}>
                  ⚠️ This section is specifically for drivers who fall <em>outside</em> of your regular motor fleet policy warranty. Please check your policy standards before submitting.
                </p>
              </div>
              <p style={{ fontSize: '14px', lineHeight: '1.7', color: '#374151', marginTop: 0 }}>
                All drivers submitted here are referred to your insurer for review. Your account handler — the Myshield Midterm team — will confirm or decline (with reason) following review.
              </p>
              <div style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 8px', fontSize: '14px', color: '#0369a1' }}>What happens next?</h4>
                <ul style={{ margin: 0, paddingLeft: '18px', lineHeight: 1.7, fontSize: '13px', color: '#374151' }}>
                  <li>The driver is submitted with a <strong>Pending Referral</strong> status.</li>
                  <li>The insurer will review the driver details including any disclosed convictions or medical conditions.</li>
                  <li>Your Myshield account handler will confirm or decline and advise you of the outcome.</li>
                </ul>
              </div>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                Please ensure all driver information is accurate and complete. Contact your Myshield account handler if you have any questions about your policy's driver acceptance criteria.
              </p>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={() => setShowWelcomePopup(false)}>
                Got it, continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content driver-form-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingDriver ? 'Edit Named Driver' : 'Add Named Driver'}</h2>
              <button className="close-btn" onClick={resetForm}>✕</button>
            </div>
            {!editingDriver && !isAdmin && (
              <div style={{ margin: '0 24px 4px', padding: '10px 14px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 6, fontSize: 13, color: '#92400e', fontWeight: 600 }}>
                ⚠️ This section is only for drivers who do not meet the policy’s standard driving warranty requirements. If you are unsure whether a driver needs to be referred or have any questions about your policy’s driving warranties, please contact your account handler.
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input type="text" required value={formData.firstName}
                    onChange={e => setFormData({ ...formData, firstName: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input type="text" required value={formData.lastName}
                    onChange={e => setFormData({ ...formData, lastName: e.target.value })} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email <span style={{ fontSize: '12px', color: '#718096', fontWeight: 400 }}>Optional</span></label>
                  <input type="email" value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Phone Number <span style={{ fontSize: '12px', color: '#718096', fontWeight: 400 }}>Optional</span></label>
                  <input type="tel" value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                </div>
              </div>

              {/* Driving Licence Images — GDPR-gated */}
              <div style={{
                marginBottom: '20px',
                padding: '14px',
                backgroundColor: licenceConsentAccepted ? '#f0fdf4' : '#fefce8',
                border: `1px solid ${licenceConsentAccepted ? '#86efac' : '#fde68a'}`,
                borderRadius: '8px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 600, fontSize: '14px', color: licenceConsentAccepted ? '#15803d' : '#92400e' }}>
                    📷 Driving Licence Images
                    {licenceConsentAccepted && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#15803d' }}>✓ Data consent given</span>
                    )}
                  </span>
                  {!licenceConsentAccepted && (
                    <button type="button" className="btn btn-secondary btn-sm"
                      style={{ fontSize: '12px' }}
                      onClick={() => setShowLicenceConsent(true)}>
                      Review &amp; Accept Data Policy
                    </button>
                  )}
                </div>
                {licenceConsentAccepted ? (
                  editingDriver ? (
                    // Edit mode — upload to existing driver
                    <div className="form-row">
                      {/* Front */}
                      <div>
                        <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Front of Licence</label>
                        {licenceFront ? (
                          <div>
                            <img src={licenceFront} alt="Licence front" style={{ maxWidth: '100%', maxHeight: '120px', objectFit: 'contain', border: '1px solid #e0f2fe', borderRadius: '6px', marginBottom: '6px', display: 'block' }} />
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '12px' }} onClick={() => licenceFrontRef.current?.click()} disabled={licenceImgUploading === 'front'}>
                                {licenceImgUploading === 'front' ? 'Uploading...' : 'Replace'}
                              </button>
                              <button type="button" onClick={() => handleLicenceImageDelete('front')} style={{ padding: '4px 10px', fontSize: '12px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: '6px', cursor: 'pointer' }}>Remove</button>
                            </div>
                          </div>
                        ) : (
                          <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '12px' }} onClick={() => licenceFrontRef.current?.click()} disabled={licenceImgUploading === 'front'}>
                            {licenceImgUploading === 'front' ? 'Uploading...' : '+ Upload Front'}
                          </button>
                        )}
                        <input ref={licenceFrontRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: 'none' }} onChange={e => handleLicenceImageUpload(e, 'front')} />
                      </div>
                      {/* Back */}
                      <div>
                        <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Back of Licence</label>
                        {licenceBack ? (
                          <div>
                            <img src={licenceBack} alt="Licence back" style={{ maxWidth: '100%', maxHeight: '120px', objectFit: 'contain', border: '1px solid #e0f2fe', borderRadius: '6px', marginBottom: '6px', display: 'block' }} />
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '12px' }} onClick={() => licenceBackRef.current?.click()} disabled={licenceImgUploading === 'back'}>
                                {licenceImgUploading === 'back' ? 'Uploading...' : 'Replace'}
                              </button>
                              <button type="button" onClick={() => handleLicenceImageDelete('back')} style={{ padding: '4px 10px', fontSize: '12px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: '6px', cursor: 'pointer' }}>Remove</button>
                            </div>
                          </div>
                        ) : (
                          <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '12px' }} onClick={() => licenceBackRef.current?.click()} disabled={licenceImgUploading === 'back'}>
                            {licenceImgUploading === 'back' ? 'Uploading...' : '+ Upload Back'}
                          </button>
                        )}
                        <input ref={licenceBackRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: 'none' }} onChange={e => handleLicenceImageUpload(e, 'back')} />
                      </div>
                    </div>
                  ) : (
                    // Add mode — stage files locally, upload after creation
                    <div className="form-row">
                      <div>
                        <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Front of Licence</label>
                        {newFrontPreview ? (
                          <div>
                            <img src={newFrontPreview} alt="Front preview" style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'contain', border: '1px solid #e0f2fe', borderRadius: '6px', marginBottom: '6px', display: 'block' }} />
                            <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '12px' }} onClick={() => { setNewFrontFile(null); setNewFrontPreview(null); }}>Remove</button>
                          </div>
                        ) : (
                          <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '12px' }} onClick={() => newFrontRef.current?.click()}>+ Select Front</button>
                        )}
                        <input ref={newFrontRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: 'none' }} onChange={e => handleNewFileSelect(e, 'front')} />
                      </div>
                      <div>
                        <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Back of Licence</label>
                        {newBackPreview ? (
                          <div>
                            <img src={newBackPreview} alt="Back preview" style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'contain', border: '1px solid #e0f2fe', borderRadius: '6px', marginBottom: '6px', display: 'block' }} />
                            <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '12px' }} onClick={() => { setNewBackFile(null); setNewBackPreview(null); }}>Remove</button>
                          </div>
                        ) : (
                          <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '12px' }} onClick={() => newBackRef.current?.click()}>+ Select Back</button>
                        )}
                        <input ref={newBackRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: 'none' }} onChange={e => handleNewFileSelect(e, 'back')} />
                      </div>
                    </div>
                  )
                ) : (
                  <p style={{ margin: 0, fontSize: '13px', color: '#92400e' }}>Accept the data policy above to upload driving licence images.</p>
                )}
                {licenceImgError && <div style={{ color: '#b91c1c', fontSize: '13px', marginTop: '8px' }}>{licenceImgError}</div>}
                {licenceConsentAccepted && <div style={{ marginTop: '8px', fontSize: '12px', color: '#0284c7' }}>PNG, JPEG, WebP or GIF · Max 5 MB · Stored securely — accessible to authorised personnel only</div>}
              </div>

              {/* Licence Restrictions */}
              <div className="form-group">
                <label>Licence Restrictions <span style={{ fontSize: '12px', color: '#718096', fontWeight: 400 }}>(e.g. must wear glasses, automatics only)</span></label>
                <input type="text" value={formData.restrictions}
                  onChange={e => setFormData({ ...formData, restrictions: e.target.value })}
                  placeholder="Enter any restrictions on the driver's licence" />
              </div>

              {/* Notifiable Medical Conditions */}
              <div style={{ marginBottom: 16, padding: '12px 14px', background: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: 8 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 600, fontSize: 14 }}>
                    Does the referred driver have any notifiable medical conditions that may affect their ability to drive safely? *
                  </label>
                  <p style={{ margin: '4px 0 8px', fontSize: 12, color: '#718096' }}>Conditions reportable to the DVLA — e.g. epilepsy, diabetes requiring insulin, sleep apnoea, visual impairment. If unsure, please contact your account handler.</p>
                  <div style={{ display: 'flex', gap: 20 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 14 }}>
                      <input type="radio" checked={formData.hasNotifiableConditions === 'yes'} onChange={() => setFormData(prev => ({ ...prev, hasNotifiableConditions: 'yes' }))} style={{ width: 16, height: 16 }} />
                      Yes
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 14 }}>
                      <input type="radio" checked={formData.hasNotifiableConditions === 'no'} onChange={() => setFormData(prev => ({ ...prev, hasNotifiableConditions: 'no' }))} style={{ width: 16, height: 16 }} />
                      No
                    </label>
                  </div>
                  {formData.hasNotifiableConditions === 'yes' && (
                    <div style={{ marginTop: 10, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 6, fontSize: 13, color: '#92400e' }}>
                      ⚠️ Please provide details in the restrictions field above and contact your Myshield account handler before submitting this driver.
                    </div>
                  )}
                </div>
              </div>

              {/* Unspent Non-Motoring Convictions */}
              <div style={{ marginBottom: 16, padding: '12px 14px', background: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: 8 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 600, fontSize: 14 }}>
                    Does the referred driver have any unspent non-motoring convictions? *
                  </label>
                  <p style={{ margin: '4px 0 8px', fontSize: 12, color: '#718096' }}>Non-motoring convictions that are not yet spent under the Rehabilitation of Offenders Act 1974. If unsure, please contact your account handler before submitting.</p>
                  <div style={{ display: 'flex', gap: 20 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 14 }}>
                      <input type="radio" checked={formData.hasUnspentConvictions === 'yes'} onChange={() => setFormData(prev => ({ ...prev, hasUnspentConvictions: 'yes' }))} style={{ width: 16, height: 16 }} />
                      Yes
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 14 }}>
                      <input type="radio" checked={formData.hasUnspentConvictions === 'no'} onChange={() => setFormData(prev => ({ ...prev, hasUnspentConvictions: 'no' }))} style={{ width: 16, height: 16 }} />
                      No
                    </label>
                  </div>
                  {formData.hasUnspentConvictions === 'yes' && (
                    <div style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        onClick={() => setShowConvictionsPopup(true)}
                        style={{ padding: '8px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 13, color: '#b91c1c', cursor: 'pointer', fontWeight: 600 }}
                      >
                        ⚠️ View important information about unspent convictions
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Accidents & Claims — new drivers only */}
              {!editingDriver && (
                <div style={{ marginBottom: 20, padding: '14px 16px', background: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: 8 }}>
                  <div className="form-group" style={{ marginBottom: formData.hasAccidentHistory === 'yes' ? 14 : 0 }}>
                    <label style={{ fontWeight: 600, fontSize: 14 }}>Has the referred driver been involved in any accidents or claims within the last 5 years, regardless of fault? *</label>
                    <div style={{ display: 'flex', gap: 20, marginTop: 8 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 14 }}>
                        <input type="radio" checked={formData.hasAccidentHistory === 'yes'} onChange={() => setFormData(prev => ({ ...prev, hasAccidentHistory: 'yes' }))} style={{ width: 16, height: 16 }} />
                        Yes
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 14 }}>
                        <input type="radio" checked={formData.hasAccidentHistory === 'no'} onChange={() => setFormData(prev => ({ ...prev, hasAccidentHistory: 'no', accidentClaims: [] }))} style={{ width: 16, height: 16 }} />
                        No
                      </label>
                    </div>
                  </div>
                  {formData.hasAccidentHistory === 'yes' && (
                    <div>
                      {formData.accidentClaims.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          {formData.accidentClaims.map((claim, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 6, fontSize: 13 }}>
                              <div style={{ flex: 1 }}>
                                <strong>{claim.claimType}</strong> — {claim.date ? new Date(claim.date).toLocaleDateString('en-GB') : '—'}
                                {claim.circumstance && <span style={{ color: '#4a5568' }}> — {claim.circumstance.slice(0, 60)}{claim.circumstance.length > 60 ? '…' : ''}</span>}
                                {claim.cost && <span style={{ color: '#718096' }}> — Cost: {claim.cost}</span>}
                              </div>
                              <button type="button" onClick={() => setFormData(prev => ({ ...prev, accidentClaims: prev.accidentClaims.filter((_, i) => i !== idx) }))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c', fontSize: 18, lineHeight: 1, padding: '0 4px' }}>×</button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ padding: '12px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8 }}>
                        <h5 style={{ margin: '0 0 10px', fontSize: 13, color: '#b91c1c' }}>Add Claim / Accident</h5>
                        <div className="form-row" style={{ marginBottom: 10 }}>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: 12 }}>Date *</label>
                            <input type="date" max={new Date().toISOString().split('T')[0]} value={currentClaim.date} onChange={e => setCurrentClaim(c => ({ ...c, date: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 13 }} />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: 12 }}>Claim Type *</label>
                            <select value={currentClaim.claimType} onChange={e => setCurrentClaim(c => ({ ...c, claimType: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                              <option value="">Select...</option>
                              <option value="Fault">Fault</option>
                              <option value="Non-Fault">Non-Fault</option>
                              <option value="Fire">Fire</option>
                              <option value="Theft">Theft</option>
                              <option value="Information Only">Information Only</option>
                            </select>
                          </div>
                        </div>
                        <div className="form-group" style={{ marginBottom: 10 }}>
                          <label style={{ fontSize: 12 }}>Circumstances *</label>
                          <textarea value={currentClaim.circumstance} onChange={e => setCurrentClaim(c => ({ ...c, circumstance: e.target.value }))} placeholder="Brief description of the accident or claim..." rows={2} style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                        </div>
                        <div className="form-group" style={{ marginBottom: 10 }}>
                          <label style={{ fontSize: 12 }}>Costs (if known)</label>
                          <input type="text" value={currentClaim.cost} onChange={e => setCurrentClaim(c => ({ ...c, cost: e.target.value }))} placeholder="e.g. £2,500 or Unknown" style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 13 }} />
                        </div>
                        <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 12 }}
                          onClick={() => {
                            if (!currentClaim.date || !currentClaim.circumstance || !currentClaim.claimType) {
                              alert('Please complete the date, circumstances and claim type before adding.');
                              return;
                            }
                            setFormData(prev => ({ ...prev, accidentClaims: [...prev.accidentClaims, currentClaim] }));
                            setCurrentClaim({ date: '', circumstance: '', claimType: '', cost: '' });
                          }}
                        >+ Add Claim</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {formError && (
                <div style={{ color: '#b91c1c', fontSize: '13px', marginBottom: '12px', padding: '8px', backgroundColor: '#fef2f2', borderRadius: '6px', border: '1px solid #fca5a5' }}>
                  {formError}
                </div>
              )}

              {/* Licence images section now integrated in the GDPR block above */}

              {/* DVLA Verification */}
              <div style={{ marginBottom: 20, padding: '14px 16px', background: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: 8 }}>
                <label style={{ fontWeight: 700, fontSize: 14, display: 'block', marginBottom: 10, color: '#002060' }}>
                  📵 DVLA Verification
                </label>

                <div className="form-group" style={{ marginBottom: formData.dvlaVerificationType ? 14 : 0 }}>
                  <label style={{ fontSize: 13 }}>Verification Method</label>
                  <select
                    value={formData.dvlaVerificationType}
                    onChange={e => setFormData(prev => ({ ...prev, dvlaVerificationType: e.target.value as '' | 'document' | 'code', dvlaCheckCode: '' }))}
                    style={{ background: '#fff' }}
                  >
                    <option value="">— Select verification method —</option>
                    <option value="document">DVLA Summary Document</option>
                    <option value="code">DVLA Driver Check Code</option>
                  </select>
                </div>

                {/* DVLA Summary Document upload */}
                {formData.dvlaVerificationType === 'document' && (
                  <div style={{ padding: '12px 14px', background: '#fff', border: '1px solid #bae6fd', borderRadius: 8 }}>
                    <p style={{ margin: '0 0 10px', fontSize: 13, color: '#0369a1' }}>
                      Upload the driver’s DVLA Summary Document (PDF or image). This will be saved with the driver record.
                    </p>
                    {dvlaDocFileName ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 20 }}>📄</span>
                        <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>{dvlaDocFileName}</span>
                        <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 12, marginLeft: 'auto' }}
                          onClick={() => { setDvlaDocFile(null); setDvlaDocFileName(''); if (dvlaDocRef.current) dvlaDocRef.current.value = ''; }}>
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 13 }}
                        onClick={() => dvlaDocRef.current?.click()}>
                        📎 Select DVLA Summary Document
                      </button>
                    )}
                    <input
                      ref={dvlaDocRef}
                      type="file"
                      accept=".pdf,image/png,image/jpeg,image/webp"
                      style={{ display: 'none' }}
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) { setDvlaDocFile(f); setDvlaDocFileName(f.name); }
                      }}
                    />
                    <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>PDF, PNG or JPEG · Max 10 MB</div>
                  </div>
                )}

                {/* DVLA Driver Check Code */}
                {formData.dvlaVerificationType === 'code' && (
                  <div style={{ padding: '12px 14px', background: '#fff', border: '1px solid #bae6fd', borderRadius: 8 }}>
                    <p style={{ margin: '0 0 10px', fontSize: 13, color: '#0369a1' }}>
                      Enter the driver’s DVLA Driving Licence check code. The driver can obtain this from{' '}
                      <a href="https://www.viewdrivingrecord.service.gov.uk/" target="_blank" rel="noopener noreferrer" style={{ color: '#0369a1' }}>View Driving Record</a>.
                    </p>
                    <input
                      type="text"
                      value={formData.dvlaCheckCode}
                      onChange={e => setFormData(prev => ({ ...prev, dvlaCheckCode: e.target.value.toUpperCase() }))}
                      placeholder="e.g. AB1CD 23EFG 45HIJ"
                      style={{
                        width: '100%', padding: '10px 12px', fontSize: 15,
                        fontFamily: 'monospace', letterSpacing: 2, fontWeight: 700,
                        border: '2px solid #3b82f6', borderRadius: 8, background: '#f0f9ff',
                        textTransform: 'uppercase',
                      }}
                      maxLength={30}
                    />
                    <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                      This is a one-time code generated by DVLA — it expires after 21 days.
                    </div>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>
                <button type="submit" className="btn btn-primary">
                  {editingDriver && !editingDriver.referralStatus ? 'Update Driver' : editingDriver ? 'Save Changes' : 'Submit for Review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Unspent Convictions Info Popup */}
      {showConvictionsPopup && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal-content" style={{ maxWidth: '460px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚠️ Unspent Non-Motoring Convictions</h2>
              <button className="close-btn" onClick={() => setShowConvictionsPopup(false)}>✕</button>
            </div>
            <div style={{ padding: '24px' }}>
              <p style={{ fontSize: '14px', lineHeight: '1.7', color: '#374151', margin: '0 0 16px' }}>
                You have declared that the driver has unspent non-motoring convictions.
              </p>
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#b91c1c', lineHeight: 1.6 }}>
                  Our team will reach out to you shortly to confirm the details of this.
                </p>
              </div>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                You can still proceed with the submission. The declaration will be reviewed as part of the referral process.
              </p>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={() => setShowConvictionsPopup(false)}>
                Understood
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Licence Consent Modal */}
      {showLicenceConsent && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal-content" style={{ maxWidth: '540px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔒 Driving Licence Data — Privacy Notice</h2>
              <button className="close-btn" onClick={handleConsentDecline}>✕</button>
            </div>
            <div style={{ padding: '24px', maxHeight: '55vh', overflowY: 'auto' }}>
              <p style={{ fontSize: '14px', lineHeight: '1.7', color: '#374151', marginTop: 0 }}>
                Before recording a driving licence number for this driver, please read and acknowledge the following:
              </p>
              <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
                <h4 style={{ margin: '0 0 6px', fontSize: '14px', color: '#111827' }}>Why we collect driving licence data</h4>
                <p style={{ margin: 0, fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
                  Driving licence numbers are collected to verify driver eligibility and manage fleet insurance compliance.
                  This information is required by insurers to ensure all named drivers are appropriately licensed.
                </p>
              </div>
              <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
                <h4 style={{ margin: '0 0 6px', fontSize: '14px', color: '#111827' }}>How we store and use this data</h4>
                <p style={{ margin: 0, fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
                  Licence data is stored securely within Myshield and is used solely for fleet management and insurance purposes.
                  It is accessible only to authorised personnel and your insurer where required.
                  Data is retained for the duration of the driver's association with the fleet and for any legally required period thereafter.
                </p>
              </div>
              <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
                <h4 style={{ margin: '0 0 6px', fontSize: '14px', color: '#111827' }}>Your rights under UK GDPR</h4>
                <p style={{ margin: 0, fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
                  The driver has the right to access, correct, or request deletion of their licence data at any time.
                  By proceeding, you confirm that the driver has been informed of and consented to this data being stored as part
                  of their inclusion on the fleet policy.
                </p>
              </div>
              <div style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#92400e', lineHeight: '1.5' }}>
                  <strong>Declaration:</strong> By clicking “Accept &amp; Continue” you confirm that this driver has been made
                  aware of and has consented to their driving licence information being stored and processed by Myshield
                  for fleet management purposes.
                </p>
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '2px solid #e2e8f0', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={handleConsentDecline}>
                Decline — don’t add licence
              </button>
              <button className="btn btn-primary" onClick={handleConsentAccept}>
                ✓ Accept &amp; Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Out-of-warranty clarification banner */}
      <div style={{ marginBottom: 20, padding: '14px 18px', background: '#fffbeb', border: '2px solid #fbbf24', borderRadius: 8 }}>
        <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#92400e', lineHeight: 1.6 }}>
          This section is specifically for drivers who fall outside of your regular motor fleet policy warranty. Please check your policy schedule before submitting. Drivers who meet the standard requirements do not need to be listed here.
        </p>
        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#b45309', lineHeight: 1.6 }}>
          At Yourshield we are here to help. If you are unsure about any of your driver warranties or where to find them, or if you would like any assistance on a new driver, please call us on <strong style={{ color: '#92400e' }}>0333 323 9660</strong>.
        </p>
      </div>

      {/* Named Approved Drivers heading */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        marginBottom: 10, paddingBottom: 8,
        borderBottom: '2px solid #e5e7eb',
      }}>
        <span style={{ fontSize: 18 }}>✅</span>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#065f46' }}>Named Approved Drivers</h3>
          <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>Drivers accepted on your motor fleet policy</p>
        </div>
        {drivers.length > 0 && (
          <span style={{
            marginLeft: 'auto', background: '#d1fae5', color: '#065f46',
            borderRadius: 9999, padding: '2px 10px', fontSize: 12, fontWeight: 700,
          }}>{drivers.length}</span>
        )}
      </div>

      {/* Driver table */}
      <div className="card">
        {drivers.length === 0 ? (
          <p className="empty-state">No named drivers found. Add your first named driver to get started.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Licence Number</th>
                  <th>Licence Expiry</th>
                  <th>Points</th>
                  <th>Penalty Codes</th>
                  <th>Restrictions</th>
                  <th>Licence</th>
                  <th>Added</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map(driver => {
                  const codes = parseCodes(driver.penaltyCodes);
                  return (
                    <tr key={driver.id}>
                      <td><strong>{driver.firstName} {driver.lastName}</strong></td>
                      <td>{driver.email}</td>
                      <td>{driver.phone}</td>
                      <td>
                        {driver.licenseNumber
                          ? <><span style={{ fontFamily: 'monospace' }}>{driver.licenseNumber}</span>{driver.licenceConsentGiven && <span title="Data consent recorded" style={{ marginLeft: 4, fontSize: '11px', color: '#15803d' }}>🔒</span>}</>
                          : <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '13px' }}>Not recorded</span>}
                      </td>
                      <td>
                        {driver.licenseExpiry ? (
                          <span className={isLicenseExpiring(driver.licenseExpiry) ? 'expiry-warning' : ''}>
                            {formatDateBritish(driver.licenseExpiry)}
                            {isLicenseExpiring(driver.licenseExpiry) && ' \u26A0\uFE0F'}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td>
                        {driver.outstandingPoints != null
                          ? <span style={{ fontWeight: 700, color: driver.outstandingPoints >= 9 ? '#b91c1c' : driver.outstandingPoints >= 6 ? '#d97706' : '#374151' }}>
                              {driver.outstandingPoints}
                            </span>
                          : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td>
                        {codes.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                            {codes.map(code => (
                              <span key={code}
                                title={getPenaltyCodeDetails(code)?.description || code}
                                style={{ padding: '2px 6px', borderRadius: '9999px', fontSize: '11px', backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', fontWeight: 600, cursor: 'default' }}>
                                {code}
                              </span>
                            ))}
                          </div>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td>
                        {driver.restrictions
                          ? <span style={{ fontSize: '13px' }} title={driver.restrictions}>
                              {driver.restrictions.length > 30 ? driver.restrictions.slice(0, 30) + '\u2026' : driver.restrictions}
                            </span>
                          : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          title="View driving licence images"
                          onClick={() => handleViewLicenceImages(driver)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '18px',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            color: '#0369a1',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#e0f2fe')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                        >
                          🪪
                        </button>
                      </td>
                      <td>{formatDateBritish(driver.createdAt)}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-icon" onClick={() => handleEdit(driver)}>✏️</button>
                          <button className="btn-icon" onClick={() => handleDelete(driver.id)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pending Named Drivers section */}
      {pendingReferrals.length > 0 && (
        <div className="card" style={{ marginTop: '28px', border: '2px solid #f59e0b', borderRadius: 10 }}>
          <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #fde68a', background: '#fffbeb', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            {/* Amber pulse dot */}
            <div style={{ position: 'relative', flexShrink: 0, marginTop: 3 }}>
              <div style={{
                width: 14, height: 14, borderRadius: '50%',
                background: '#f59e0b',
                boxShadow: '0 0 0 0 rgba(245,158,11,0.4)',
                animation: 'pulse-amber 1.8s infinite',
              }} />
              <style>{`@keyframes pulse-amber { 0%{box-shadow:0 0 0 0 rgba(245,158,11,0.5)} 70%{box-shadow:0 0 0 10px rgba(245,158,11,0)} 100%{box-shadow:0 0 0 0 rgba(245,158,11,0)} }`}</style>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#92400e' }}>Pending Named Drivers</h3>
                <span style={{ background: '#f59e0b', color: '#fff', borderRadius: 9999, padding: '1px 8px', fontSize: 12, fontWeight: 700 }}>
                  {pendingReferrals.length}
                </span>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#a16207', lineHeight: 1.6 }}>
                The following drivers are pending referral acceptance by your insurer — the Myshield Midterm team will be in touch within the next 48 hours to confirm if the driver is accepted or declined following the insurers review. If you require an urgent update please contact the team on <strong style={{ color: '#92400e' }}>0333 323 9660</strong>.
              </p>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  {!clientIdFilter && <th>Company</th>}
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Submitted</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingReferrals.map(driver => (
                  <tr key={driver.id}>
                    <td><strong>{driver.firstName} {driver.lastName}</strong></td>
                    {!clientIdFilter && (
                      <td style={{ fontSize: 13, color: '#374151' }}>
                        {driver.client?.companyName || <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                    )}
                    <td>{driver.email}</td>
                    <td>{driver.phone}</td>
                    <td>{formatDateBritish(driver.createdAt)}</td>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '3px 10px', borderRadius: '9999px', fontSize: '12px',
                        backgroundColor: '#fef3c7', color: '#92400e',
                        border: '1px solid #fde68a', fontWeight: 600,
                      }}>
                        ⏳ Pending Referral
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Licence Images Viewer Modal */}
      {licenceView && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2>🪪 Licence Images — {licenceView.driverName}</h2>
              <button className="close-btn" onClick={() => setLicenceView(null)}>✕</button>
            </div>
            <div style={{ padding: '20px' }}>
              {licenceView.loading ? (
                <p style={{ textAlign: 'center', color: '#718096', padding: '24px' }}>Loading images...</p>
              ) : (!licenceView.front && !licenceView.back) ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📷</div>
                  <p style={{ margin: 0, fontWeight: 500 }}>No licence images uploaded for this driver.</p>
                  <p style={{ margin: '8px 0 0', fontSize: 13 }}>Use the edit button to upload front and back licence images.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#374151' }}>Front of Licence</h4>
                    {licenceView.front
                      ? <img src={licenceView.front} alt="Licence front" style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', border: '1px solid #bae6fd', borderRadius: '8px' }} />
                      : <div style={{ padding: '40px', textAlign: 'center', background: '#f8fafc', borderRadius: '8px', color: '#9ca3af', fontSize: 13 }}>Not uploaded</div>
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#374151' }}>Back of Licence</h4>
                    {licenceView.back
                      ? <img src={licenceView.back} alt="Licence back" style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', border: '1px solid #bae6fd', borderRadius: '8px' }} />
                      : <div style={{ padding: '40px', textAlign: 'center', background: '#f8fafc', borderRadius: '8px', color: '#9ca3af', fontSize: 13 }}>Not uploaded</div>
                    }
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverList;
